import type { DamageType } from "../../damageType";
import type { ParticleType } from "../../particleType";
import { PhysicalThing } from "../../physicalThing";
import { STD_BASE_HP } from "../../physicalThingWithHp";
import { deserializeXY } from "../../serialization";
import type { Thing } from "../../thing";
import { XY } from "../../xy";
import type { MultiTileThingController } from "./multiTileThingController";

export abstract class MultiTileThingCore extends PhysicalThing {
	hp: number = STD_BASE_HP;
	maxHp: number = STD_BASE_HP;
	damageMultipliers: Map<DamageType, number> = new Map();
	controller: MultiTileThingController | null = null;

	constructor(blocksMovement: boolean = true) {
		super(new XY(0, 0), blocksMovement);
	}

	getHp(): number {
		return this.hp;
	}

	getMaxHp(): number {
		return this.maxHp;
	}

	getBloodParticleType(): ParticleType | null {
		return null;
	}

	applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		if (!this.active) {
			throw new Error("Attempting to damage something inactive");
		}
		const multiplier = type !== null ? (this.damageMultipliers.get(type) ?? 1.0) : 1.0;
		const effectiveDmg = Math.floor(amount * multiplier);
		let newHp = Math.max(this.hp - effectiveDmg, 0);
		newHp = Math.min(newHp, this.maxHp);
		this.hp = newHp;
		if (newHp <= 0) {
			this.onZeroHp(source);
		}
		return effectiveDmg;
	}

	protected onZeroHp(_source: Thing | null): void {
		if (this.controller) {
			for (const part of this.controller.getParts()) {
				this.board!.removeAtEndOfTurn(part);
			}
		}
		this.board!.removeAtEndOfTurn(this);
	}

	isHittable(): boolean {
		return false;
	}

	onProjectileHit(_projectile: unknown): void {
		// no-op — subclasses may override
	}

	isMovementReady(): boolean {
		return true;
	}

	protected restoreCoreFields(data: any): void {
		if (data.xy) {
			this.xy = deserializeXY(data.xy);
		}
		this.visualId = data.visualId;
		this.hp = data.hp;
		this.maxHp = data.maxHp;
		if (data.damageMultipliers) {
			this.damageMultipliers = new Map(data.damageMultipliers);
		}
	}

	abstract override getZOrder(): number;
	override isVisible(): boolean {
		return true;
	}
	abstract override isHideUnderneath(): boolean;
	abstract isPassable(): boolean;

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			hp: this.hp,
			maxHp: this.maxHp,
			damageMultipliers: [...this.damageMultipliers.entries()],
		};
	}
}
